import React, { useState, useEffect } from 'react';
import { Mail, Phone, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import './Contact.css';
import './ContactMobile.css';
import { updateMetaTags } from '../utils/seo';
import { CONTACT_API_URL } from '../utils/api';
import Navbar from '../Navbar';

const Contact = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        updateMetaTags({
            title: "Contact Us | Get in Touch",
            description: "Contact NeuZenAI for expert AI consulting and custom product engineering. Let's start your digital transformation journey today.",
            url: "https://neuzenai.com/contact",
        });
    }, []);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: ''
    });

    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch(CONTACT_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: formData.phone,
                    company: formData.company,
                    message: "Contact Page Submission",
                    source: "contact_page"
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setIsSubmitted(true);
                setTimeout(() => {
                    setIsSubmitted(false);
                    setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        company: ''
                    });
                }, 5000);
            } else {
                const errorMsg = data.message || 'Failed to send message. Please try again.';
                alert(errorMsg);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('An error occurred. Not connected to server.');
        }
    };

    const offices = [
        {
            city: "Dallas, U.S.A",
            address: "702 S Denton Tap Rd, Suite # 110, Coppell, 75019, Texas, USA",
            email: "contact@neuzenai.com",
            phone: "+1 (972) 992-5082",
            mapLink: "#"
        },
        {
            city: "Hyderabad, India",
            address: "T Hub Phase 2, In orbit Mall Rd, Madhapur, Hyderabad, Telangana 500081, India",
            email: "contact@neuzenai.com",
            phone: "+91 88852 57422",
            mapLink: "#"
        }
    ];

    return (
        <div className="contact-page">
            <Navbar />
            <section className="contact-header-section">
                <div className="container mx-auto px-4 text-center">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="contact-main-title"
                    >
                        Take The <span className="text-black">Next Step</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="contact-sub-title"
                    >
                        Feel free to get in touch for any enquiry, support or general talk. Our Agent will get in touch with you with-in next 24 hours
                    </motion.p>
                </div>
            </section>

            <section className="contact-main-section">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="contact-card-wrapper">
                        <div className="contact-card-v2">
                            <div className="contact-card-left">
                                <div className="card-left-content">
                                    <h2 className="card-left-title">Speak With <span className="text-black">An Expert</span></h2>
                                    <p className="card-left-desc">
                                        Schedule a call with us to explore tailored data-driven solutions today!
                                    </p>
                                    <button 
                                        type="button" 
                                        className="schedule-btn"
                                        onClick={() => window.open("https://calendly.com/contact-neuzenai/30min", "_blank")}
                                    >
                                        Book a Demo
                                    </button>
                                </div>
                                <div className="corner-circles">
                                    <div className="circle circle-1"></div>
                                    <div className="circle circle-2"></div>
                                </div>
                            </div>

                            <div className="contact-card-right">
                                {isSubmitted ? (
                                    <div className="submission-success">
                                        <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                                        <h3>Thank You!</h3>
                                        <p>We have received your message and will get back to you soon.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="contact-form-v2">
                                        <div className="form-row-compact">
                                            <div className="form-group-compact">
                                                <label>First Name *</label>
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group-compact">
                                                <label>Last Name *</label>
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group-compact">
                                            <label>Work Email *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="form-group-compact">
                                            <label>Phone Number</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="form-group-compact">
                                            <label>Company Name *</label>
                                            <input
                                                type="text"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="form-submit-row">
                                            <button type="submit" className="form-submit-btn">Submit</button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="offices-section pt-4 pb-20">
                <div className="container mx-auto px-4 max-w-6xl">
                    <h2 id="offices-heading" className="section-title text-center mb-12">Our Offices</h2>
                    <div className="offices-grid">
                        {offices.map((office, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="office-card"
                            >
                                <h3 className="office-city">{office.city}</h3>
                                <p className="office-address">{office.address}</p>
                                <div className="office-contact">
                                    <div className="contact-item">
                                        <Mail className="w-5 h-5" />
                                        <a href={`mailto:${office.email}`} className="email-link">{office.email}</a>
                                    </div>
                                    <div className="contact-item">
                                        <Phone className="w-5 h-5" />
                                        <span>{office.phone}</span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
